import React, { useId } from 'react';
import { AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/**
 * Phase 19 F25: the collapsible card and config-driven field grid the shared care plan sections
 * are built from. Field keys are the care plan's flat `formData` keys; the plan still owns its
 * state, validation and messages, and passes them in.
 */

export type SectionTone = 'blue' | 'cyan' | 'gray' | 'indigo' | 'pink' | 'purple' | 'red' | 'slate';

// Full class strings so Tailwind keeps them.
const TONES: Record<SectionTone, { card: string; hover: string; title: string; icon: string; divider: string }> = {
  blue: { card: 'bg-blue-50 border-blue-200', hover: 'hover:bg-blue-100', title: 'text-blue-900', icon: 'text-blue-600', divider: 'border-blue-200' },
  cyan: { card: 'bg-cyan-50 border-cyan-200', hover: 'hover:bg-cyan-100', title: 'text-cyan-900', icon: 'text-cyan-600', divider: 'border-cyan-200' },
  gray: { card: 'bg-gray-50 border-gray-200', hover: 'hover:bg-gray-100', title: 'text-gray-900', icon: 'text-blue-600', divider: 'border-gray-200' },
  indigo: { card: 'bg-indigo-50 border-indigo-200', hover: 'hover:bg-indigo-100', title: 'text-indigo-900', icon: 'text-indigo-600', divider: 'border-indigo-200' },
  pink: { card: 'bg-pink-50 border-pink-200', hover: 'hover:bg-pink-100', title: 'text-pink-900', icon: 'text-pink-600', divider: 'border-pink-200' },
  purple: { card: 'bg-purple-50 border-purple-200', hover: 'hover:bg-purple-100', title: 'text-purple-900', icon: 'text-purple-600', divider: 'border-purple-200' },
  red: { card: 'bg-red-50 border-red-200', hover: 'hover:bg-red-100', title: 'text-red-900', icon: 'text-red-600', divider: 'border-red-200' },
  slate: { card: 'bg-slate-50 border-slate-200', hover: 'hover:bg-slate-100', title: 'text-slate-900', icon: 'text-slate-600', divider: 'border-slate-200' },
};

interface SectionCardProps {
  title: string;
  icon?: LucideIcon | null;
  tone: SectionTone;
  collapsed?: boolean;
  onToggle?: () => void;
  /** Shown under the title while collapsed (e.g. the physician's name) */
  summary?: string;
  children: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({ title, icon: Icon, tone, collapsed = false, onToggle, summary, children }) => {
  const t = TONES[tone];
  return (
    <div className={`rounded-lg border ${t.card}`}>
      <button
        type="button"
        className={`w-full p-4 flex justify-between items-center text-left rounded-t-lg transition-colors ${t.hover}`}
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <span className="flex items-center gap-3">
          {Icon && <Icon size={18} className={t.icon} aria-hidden="true" />}
          <span>
            <span className={`block text-md font-medium ${t.title}`}>{title}</span>
            {collapsed && summary && <span className={`block text-xs mt-0.5 ${t.icon}`}>{summary}</span>}
          </span>
        </span>
        {collapsed
          ? <ChevronDown size={20} className={t.icon} aria-hidden="true" />
          : <ChevronUp size={20} className={t.icon} aria-hidden="true" />}
      </button>
      {!collapsed && <div className={`p-4 border-t ${t.divider}`}>{children}</div>}
    </div>
  );
};

export type SectionFieldValue = string | boolean | string[];

export interface SectionFieldConfig {
  /** formData key (for `checkboxes`, a group id; each item names its own boolean key) */
  key: string;
  label: string;
  kind?: 'text' | 'tel' | 'date' | 'textarea' | 'select' | 'checkboxes' | 'checklist';
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  /** Spans the full grid width */
  wide?: boolean;
  hint?: string;
  /** Label read by screen readers only (the card title already names the field) */
  srOnlyLabel?: boolean;
  /** `select` options (first is usually the empty "Select ..." option) and `checklist` choices */
  options?: Array<{ value: string; label: string }>;
  /** `checkboxes`: one boolean formData key per box */
  items?: Array<{ key: string; label: string }>;
}

export interface SectionFieldsProps {
  values: Readonly<Record<string, unknown>>;
  // Method syntax: plans pass their own handleChange, typed for the values they use.
  onChange(field: string, value: SectionFieldValue): void;
  errors?: Record<string, string>;
  readOnly?: boolean;
}

const inputClass = (error: boolean, readOnly: boolean) =>
  `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-gray-300'} ${readOnly ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}`;

export const FieldError: React.FC<{ message?: string }> = ({ message }) => (
  message ? <p className="text-red-500 text-sm mt-1 flex items-center"><AlertCircle size={16} className="mr-1" aria-hidden="true" />{message}</p> : null
);

const asString = (v: unknown) => (typeof v === 'string' ? v : '');

const SectionField: React.FC<SectionFieldsProps & { field: SectionFieldConfig; id: string }> = ({ field: f, id, values, onChange, errors = {}, readOnly = false }) => {
  const error = errors[f.key];
  const label = `${f.label}${f.required ? ' *' : ''}`;
  const kind = f.kind || 'text';

  if (kind === 'checkboxes' || kind === 'checklist') {
    const selected = Array.isArray(values[f.key]) ? (values[f.key] as string[]) : [];
    const boxes = kind === 'checkboxes'
      ? (f.items || []).map(item => ({ id: item.key, label: item.label, checked: values[item.key] === true, set: (on: boolean) => onChange(item.key, on) }))
      : (f.options || []).map(o => ({
        id: o.value, label: o.label, checked: selected.includes(o.value),
        set: (on: boolean) => onChange(f.key, on ? [...selected, o.value] : selected.filter(v => v !== o.value)),
      }));
    return (
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-2">{label}</legend>
        <div className="space-y-2">
          {boxes.map(b => (
            <label key={b.id} className={`flex items-center space-x-2 text-sm text-gray-700 ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" disabled={readOnly}
                checked={b.checked} onChange={e => b.set(e.target.checked)} />
              <span>{b.label}</span>
            </label>
          ))}
        </div>
        <FieldError message={error} />
      </fieldset>
    );
  }

  const common = {
    id,
    disabled: readOnly,
    value: asString(values[f.key]),
    'aria-invalid': Boolean(error),
    className: inputClass(Boolean(error), readOnly),
  };
  let control: React.ReactNode;
  if (kind === 'textarea') {
    control = <textarea {...common} rows={f.rows || 3} placeholder={f.placeholder} onChange={e => onChange(f.key, e.target.value)} />;
  } else if (kind === 'select') {
    control = (
      <select {...common} onChange={e => onChange(f.key, e.target.value)}>
        {(f.options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else {
    control = <input {...common} type={kind} maxLength={f.maxLength} placeholder={f.placeholder} onChange={e => onChange(f.key, e.target.value)} />;
  }
  return (
    <div>
      <label htmlFor={id} className={f.srOnlyLabel ? 'sr-only' : 'block text-sm font-medium text-gray-700 mb-1'}>{label}</label>
      {control}
      {f.hint && <p className="text-xs text-gray-500 mt-1">{f.hint}</p>}
      <FieldError message={error} />
    </div>
  );
};

const GRID_COLS: Record<1 | 2 | 3, string> = { 1: 'grid grid-cols-1 gap-4', 2: 'grid grid-cols-1 md:grid-cols-2 gap-4', 3: 'grid grid-cols-1 md:grid-cols-3 gap-4' };
const WIDE: Record<1 | 2 | 3, string> = { 1: '', 2: 'md:col-span-2', 3: 'md:col-span-3' };

/** A grid of care plan fields; each field reads `values[key]` and writes through `onChange(key, value)`. */
export const SectionFields: React.FC<SectionFieldsProps & { fields: SectionFieldConfig[]; columns?: 1 | 2 | 3 }> = ({ fields, columns = 2, ...rest }) => {
  const baseId = useId();
  return (
    <div className={GRID_COLS[columns]}>
      {fields.map(f => (
        <div key={f.key} className={f.wide ? WIDE[columns] || undefined : undefined}>
          <SectionField field={f} id={`${baseId}-${f.key}`} {...rest} />
        </div>
      ))}
    </div>
  );
};

/** Field list with per-plan overrides applied by key (labels, placeholders, required marks). */
export function withOverrides(fields: SectionFieldConfig[], overrides?: Partial<Record<string, Partial<SectionFieldConfig>>>): SectionFieldConfig[] {
  if (!overrides) return fields;
  return fields.map(f => (overrides[f.key] ? { ...f, ...overrides[f.key] } : f));
}
